import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../core/services/api/auth.service';

export const DEFAULT_AUTHORIZED_ROLES = ['authenticated', 'admin'];

/**
 * Restreint l'accès à une route en fonction du `type` du rôle Strapi de l'utilisateur courant.
 *
 * Déclaration côté route :
 * ```ts
 * { path: 'admin', component: AdminCmp, canActivate: [authGuard, roleGuard], data: { roles: ['admin'] } }
 * ```
 *
 * Conventions :
 *  - `data.roles` est comparé à `user.role.type` (clé canonique Strapi, indépendante de la langue).
 *  - Si la route ne déclare pas `data.roles` ou un tableau vide, l'accès est autorisé.
 *  - Si l'utilisateur n'est pas authentifié, redirection vers `/login`.
 *  - Sinon, redirection vers `/projects` (la home applicative).
 */
export const roleGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  const requiredRoles = (route.data['roles'] as string[] | undefined) ?? DEFAULT_AUTHORIZED_ROLES;
  if (requiredRoles.length === 0) {
    return true;
  }

  if (!auth.isAuthenticated()) {
    return router.createUrlTree(['/login'], {
      queryParams: { returnUrl: state.url },
    });
  }

  // Some auth payloads may not include role details immediately.
  // In that case, treat user as standard authenticated to avoid login loops.
  const currentRoleType = auth.getCurrentUserRoleType() ?? 'authenticated';
  if (requiredRoles.includes(currentRoleType)) {
    return true;
  }

  return router.createUrlTree(['/forbidden']);
};
